import React, { useEffect, useState } from "react";
import { ContentMainLayout } from "../../layout/contentLayout";
import { Box, Button, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";


import BadgeImage from "../ui-elements/badge-image";
import ImageGrid from "../image-grid";
import CommentContainer from "../ui-elements/comment-container";
import TypographyContent from "../ui-elements/typography-content";
import {
  PostContentDetails,
  PostContentDetailsDummy,
} from "../../interface/posts";
import {
  formatDateAndTimeFromString,
  formatTextType,
} from "../../utils/date-setup";
import CommentUI from "../ui-elements/comment-ui";
import { CommentData, CommentReplyPropsData } from "../../interface/comment";
import { organizeCommentsByPost } from "../../utils/comment";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import { apiBookmarkPost } from "../../api/post";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

const UnAuthenticatedReport = ({
  post_details,
  comment_data,
}: {
  post_details: PostContentDetails;
  comment_data: CommentData[];
}) => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isMedium = useResponsive("down", "md", "xl");
  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const [comment_list, setComment_list] = useState<CommentReplyPropsData[]>([]);
  const [reply_state, setReply_state] = useState({
    reply_modal: false,
    reply_data: {},
  });

  const handle_open_reply = (reply_data: CommentReplyPropsData) => {
    setReply_state((prev) => ({
      ...prev,
      reply_modal: true,
      reply_data: reply_data,
    }));
  };

  const handle_close_reply = () => {
    setReply_state((prev) => ({ ...prev, reply_modal: false }));
  };

  useEffect(() => {
    const fetchComment = async () => {
      // filter comment based on post
      const organizedComments = organizeCommentsByPost(
        comment_data,
        post_details?.id
      );
      setComment_list(organizedComments);
    };
    fetchComment();
  }, []);

  // add comments
  const handle_add_comment = async (message: string) => {
    //  add one to current list
    const newComment: CommentReplyPropsData = {
      id: Math.floor(Math.random() * 100),
      parent_id: null,
      post_id: post_details?.id,
      author_id: account_profile_data?.id,
      author_name: account_profile_data?.name,
      replies: [],
      author_photo_url: account_profile_data?.image,
      comment: message,
      likes: 0,
      commented_at: new Date().toISOString(),
    };

    // add to list
    setComment_list((prev) => [...prev, newComment]);
    // send request
  };

  const handle_add_reply = async (reply_data: CommentReplyPropsData) => {
    // Step 1: Find the parent comment using parent_id
    setComment_list((prev) => {
      return prev.map((comment) => {
        if (comment.id === reply_data.parent_id) {
          // Step 2: Update the replies array by adding the new reply
          return {
            ...comment,
            replies: [...comment.replies, reply_data], // Add the new reply
          };
        }
        // Return the original comment if it's not the target
        return comment;
      });
    });
  };

  return (
    <ContentMainLayout>
      <Stack direction={"row"} spacing={4} justifyContent={"center"}>
        <Stack width={isDesktop ? "90%" : "65%"}>
          <Box
            id="main-scroll-container"
            sx={{
              width: isMedium ? "100%" : "95%",
              mx: 2,
              mt: "auto",
              height: "76vh",
              overflowY: "auto",
              pt: "85px",
              px: isMedium ? 1 : 3,
              position: "relative",
            }}
          >
            <ReportContentDetails
              post_details={post_details}
              handle_open_reply={handle_open_reply}
              comment_list={comment_list}
            />
            <Box
              sx={{
                position: "fixed",
                width: isMedium ? "80%" : 600,
                mx: "auto",
                bottom: 10,
                p: 1,
                py: 2,
                backgroundColor: "#FAFAFA",
              }}
            ></Box>
          </Box>
        </Stack>
      </Stack>
    </ContentMainLayout>
  );
};

export default UnAuthenticatedReport;

const ReportContentDetails = ({
  post_details,
  comment_list,
  handle_open_reply,
}: {
  post_details: PostContentDetails;
  handle_open_reply: (e: any) => void;
  comment_list: CommentReplyPropsData[];
}) => {
  return (
    <>
      <Box sx={{ pb: "4rem" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 3,
          }}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={2}>
            <img
              src={post_details?.author_photo_url}
              style={{ width: 50, height: 50, borderRadius: 40 }}
            />
            <Stack>
              <Stack direction={"row"} spacing={1}>
                <Typography variant="subtitle2" color="black">
                  {post_details?.author}
                </Typography>
                <BadgeImage badge={post_details?.author_badge} />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: "#4A5E6D",
                  fontWeight: 400,
                }}
              >
                {formatDateAndTimeFromString(post_details?.created_at)}
              </Typography>
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  fontWeight: 400,
                  fontStyle: "italic",
                }}
              >
                {formatTextType(post_details?.post_type)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ ml: 4 }}>
          <Stack>
            <Typography
              color="black"
              sx={{
                fontSize: 18,
                marginBottom: 2,
                fontWeight: 500,
              }}
            >
              {post_details?.title}
            </Typography>
            <ImageGrid
              images={post_details?.media} // Pass images as an array
            />
          </Stack>
          <Box sx={{ mt: "10px", display: "block" }}>
            <TypographyContent content={post_details?.context} />
          </Box>
        </Box>
        {comment_list?.length > 0 &&
          comment_list?.map((comment, index) => (<></>
            // <CommentUI
            //   key={index}
            //   id={comment?.id}
            //   author_img={comment?.author_photo_url}
            //   author_name={comment?.author_name}
            //   date={comment?.commented_at}
            //   comment={comment?.comment}
            //   likes={comment?.likes}
            //   sx={{}}
            //   handle_open_reply={handle_open_reply}
            //   replies={comment?.replies} // Optional since replies may be undefined
            // />
          ))}
      </Box>
    </>
  );
};
